import { format } from 'date-fns';
import { IStudentPayment } from '../models/payments.model';
import {
  IPaymentAmount,
  ISelectedCoupon,
  paymentTargetCode,
} from '../models/studentStudent.model';
import moment from 'moment';
import { ICode, ISubcode } from '../models/codes.model';
import { ICampusItem, ICampusGroup } from '../models/campuses.model';
// @ts-ignore
import XlsxPopulate from 'xlsx-populate';
import _ from 'lodash';
import QueryString from 'qs';

export const getKey = (): number => {
  return new Date().getTime() + Math.random();
};

export const getDate = (date: string): Date => {
  return new Date(
    date.slice(0, 4) + '-' + date.slice(4, 6) + '-' + date.slice(6, 8),
  );
};

export const getDateFormat = (date: string): string => {
  return format(getDate(date), 'yyyy.MM.dd');
};

export const getDateFullFormat = (date: string): string => {
  return format(new Date(date.replaceAll('.', '-')), 'yyyy.MM.dd HH:mm:ss');
};

export const createEmptyNumberArray = (length: number): number[] => {
  return Array.from({ length }, () => 0);
};

export const summaryPrice = (
  data: IStudentPayment[],
  key: keyof IStudentPayment,
): number => {
  return data.reduce(
    (previousValue, currentValue) => previousValue + +currentValue[key],
    0,
  );
};

export const getInstallment = (length = 11): string[] => {
  return Array.from({ length }, (v, k) => `${k + 2} 개월`);
};

export const getTargetName = (code: paymentTargetCode): string => {
  switch (code) {
    case 'TU':
      return '수강료';
    case 'ON':
      return '온라인 수강료';
    case 'ST':
      return '셔틀비';
  }
};

export const getCouponDiscount = (
  sortedStudentPayments: IStudentPayment[],
  couponDiscounts: ISelectedCoupon[],
  index: number,
): number => {
  const { paymentTargetCode, paymentNo } = sortedStudentPayments[index];
  return couponDiscounts
    .filter(
      (item) =>
        item.paymentTargetCode === paymentTargetCode &&
        item.paymentNo === paymentNo,
    )
    .reduce(
      (previousValue, currentValue) => previousValue + currentValue.amount,
      0,
    );
};

export const modifiedGetCouponDiscount = (
  studentPayment: IStudentPayment,
  couponDiscounts: ISelectedCoupon[],
): number => {
  const { paymentTargetCode, paymentNo } = studentPayment;
  return couponDiscounts
    .filter(
      (item) =>
        item.paymentTargetCode === paymentTargetCode &&
        item.paymentNo === paymentNo,
    )
    .reduce(
      (previousValue, currentValue) => previousValue + currentValue.amount,
      0,
    );
};

export const getPaymentAmount = (
  data: IPaymentAmount[],
  filter: (data: IStudentPayment | IPaymentAmount) => boolean,
): number => {
  return data
    .filter(filter)
    .filter(({ checked }) => checked)
    .reduce(
      (previousValue, currentValue) => previousValue + currentValue.amount,
      0,
    );
};

export const addComma = (str: string) =>
  str.replace(/(\d)(?=(?:\d{3})+(?!\d))/g, '$1,');

export const removeComma = (str: string) => str.replace(/[^\d]+/g, '');

// 금액 입력 처리 (comma)
export const formatAmount = (amount: string) => addComma(removeComma(amount));

// 요일 구하기
export const getDayOfWeek = (date: string): string => {
  switch (moment(date).day()) {
    case 0:
      return '일';
    case 1:
      return '월';
    case 2:
      return '화';
    case 3:
      return '수';
    case 4:
      return '목';
    case 5:
      return '금';
    case 6:
      return '토';
    default:
      return '-';
  }
};

export const getVat = (amount: number): number => {
  return Math.floor(amount / 10 / 1.1);
};

export const getDetailCode = (codes: ICode[], div: string): ISubcode[] =>
  codes.find(({ commonCode }) => commonCode === div)?.detailCode ?? [];

export const getDetailCodeLabel = (
  subCodes: ISubcode[],
  div: string,
): string => {
  return subCodes.find(({ detailCode }) => detailCode === div)?.korName ?? div;
};

export const getMaxAmount = (
  baseAmount: number,
  couponAmount: number,
): number => (baseAmount <= couponAmount ? baseAmount : couponAmount);

export const getMinAmount = (couponAmount: number) =>
  couponAmount <= 0 ? 0 : couponAmount;

export const antdTableStrSorter = <T>(a: T, b: T, key: keyof T): number =>
  String(a[key])?.localeCompare(String(b[key]));

export const getRank = (
  score: number,
  eduStepCode: string = 'EM',
) => {
  if (eduStepCode === 'EM') {
    // 초등
    if (score >= 90) {
      return 'A';
    } else if (score >= 80) {
      return 'B';
    } else if (score >= 60) {
      return 'C';
    } else if (score >= 10) {
      return 'D';
    } else {
      return 'F';
    }  
  } else {
    // 중등
    if (score >= 80) {
      return 'Excellent';
    } else if (score >= 60) {
      return 'Good';
    } else if (score >= 40) {
      return 'Average';
    } else if (score >= 20) {
      return 'Fair';
    } else {
      return 'Poor';
    }
  }
};

// 전화번호 하이픈 추가
export const addHyphen = (phone: string | undefined): string => {
  if (!phone) return '';
  return phone.replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, '$1-$2-$3');
};

export const getWorkbook = (filename: string) => {
  return new Promise(function (resolve, reject) {
    var req = new XMLHttpRequest();
    req.open('GET', `/${filename}`, true);
    req.responseType = 'arraybuffer';
    req.onreadystatechange = function () {
      if (req.readyState === 4) {
        if (req.status === 200) {
          resolve(XlsxPopulate.fromDataAsync(req.response));
        } else {
          reject('Received a ' + req.status + ' HTTP code.');
        }
      }
    };

    req.send();
  });
};

export const phoneValidation = (phone: string): boolean => {
  return /^\d{11}$/.test(phone);
};

export const getAxiosBaseUrl = () => {
  return process.env.REACT_APP_IS_DEV === 'Y'
    ? process.env.REACT_APP_DEV_AXIOS_HOST
    : process.env.REACT_APP_AXIOS_HOST;
};

export const getWebUrl = (): string =>
  process.env.REACT_APP_IS_DEV === 'Y'
    ? process.env.REACT_APP_DEV_WWW_URL ?? ''
    : process.env.REACT_APP_WWW_URL ?? '';

export const getCanbOnUrl = () => {
  return process.env.REACT_APP_IS_DEV === 'Y'
    ? process.env.REACT_APP_DEV_CANBON_URL
    : process.env.REACT_APP_CANBON_URL;
};

export const getAiCareUrl = () => {
  return process.env.REACT_APP_IS_DEV === 'Y'
    ? process.env.REACT_APP_DEV_AI_CARE_URL
    : process.env.REACT_APP_AI_CARE_URL;
};

export const handleEmptyString = (obj: any) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] === '') {
      obj[key] = undefined;
    }
  });

  return obj;
};

export const getReverseYears = (SY0005: any) => {
  if (!SY0005) return [];
  return _.orderBy(SY0005, ['orderSeq'], ['desc']);
};

export const formatOneDecimal = (value: any, getNumber: boolean = false) => {
  const n = parseFloat(value);
  if (isNaN(n)) {
    return getNumber ? 0 : '';
  }
  const result = n.toFixed(n % 1 === 0 ? 0 : 1);
  return getNumber ? Number(result) : result;
};

export const qsParse = (locationSearch: any) => {
  return QueryString.parse(locationSearch, { ignoreQueryPrefix: true });
};

export const addAllOption = (data: any, language: string = 'ko') => {
  const arr = data || [];
  const label = language === 'ko' ? '전체' : 'All';
  return [{ label, value: '' }, ...arr];
};

export const getOptions = (
  data: any,
  label: string,
  value: string,
  includeAll: boolean = false,
  language: string = 'ko'
) => {
  let arr = includeAll ? addAllOption(data, language) : data;
  const options = arr?.map((item: any) => ({
    label: String(item[label] || item.label),
    value: String(item[value] || item.value),
  }));
  return options;
};

export const getPublisherGradeCodes = (schoolGbn: string) => {
  return schoolGbn === 'ES'
    ? ['01', '02', '03']
    : schoolGbn === 'MS'
      ? ['07', '08', '09']
      : ['10', '11', '12'];
};

export const getItemNumber = (
  pageNo: number,
  pageSize: number,
  index: number,
  total?: number
) => {
  if (total) {
    // 역순
    return total - ((pageNo - 1) * pageSize + index);
  }

  return (pageNo - 1) * pageSize + index + 1;
};



export const getCampusGroup = (
  campuses: ICampusItem[], 
  campusHeadOffice?: string,
): ICampusGroup[] => {

  const checkTestCampus = (campus: any) => campus.testYn === 'N' && campus.campusCode !== campusHeadOffice;
  const checkNoTestCampus = (campus: any) => campus.testYn === 'Y' || campus.campusCode === campusHeadOffice;
  const sort = (campuses: ICampusItem[]) => {
    return [...campuses].sort((a: ICampusItem, b: ICampusItem) => 
      a.campusCode < b.campusCode ? -1 : a.campusCode < b.campusCode ? 1 : 0);
  }

  campuses = sort(campuses);

  return [
    { isTestCampus: false, campuses: [...campuses].filter(checkTestCampus) }, 
    { isTestCampus: true,  campuses: [...campuses].filter(checkNoTestCampus) }
  ];
}
